import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  ArrowLeft, Car, Home, FileText, DollarSign, Calendar,
  Clock, CheckCircle, AlertTriangle, RefreshCw, Search,
  Eye, Printer, TrendingUp, Users, Filter
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminFinanceOrders = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  const [autoOrders, setAutoOrders] = useState([]);
  const [mortgageOrders, setMortgageOrders] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    auto: 0,
    mortgage: 0,
    pending: 0,
    active: 0,
    total_principal: 0
  });

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    if (user === null) return;
    if (!user?.is_admin) {
      toast.error('Admin access required');
      navigate('/dashboard');
      return;
    }
    fetchAllOrders();
  }, [token, user, navigate]);

  const fetchAllOrders = async () => {
    setLoading(true);
    try {
      const [autoRes, mortgageRes] = await Promise.all([
        fetch(`${API}/admin/auto-financing/orders`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API}/admin/mortgage-refinance/orders`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      let autos = [];
      let mortgages = [];

      if (autoRes.ok) {
        const autoData = await autoRes.json();
        autos = (autoData.orders || []).map(o => ({ ...o, type: 'auto' }));
      }

      if (mortgageRes.ok) {
        const mortgageData = await mortgageRes.json();
        mortgages = (mortgageData.orders || []).map(o => ({ ...o, type: 'mortgage' }));
      }

      setAutoOrders(autos);
      setMortgageOrders(mortgages);

      // Calculate stats
      const all = [...autos, ...mortgages];
      setStats({
        total: all.length,
        auto: autos.length,
        mortgage: mortgages.length,
        pending: all.filter(o => o.status === 'pending' || o.status === 'pending_consent').length,
        active: all.filter(o => o.status === 'active').length,
        total_principal: all.reduce((sum, o) => sum + (o.principal_amount || 0), 0)
      });

    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to load finance orders');
    } finally {
      setLoading(false);
    }
  };

  const getAllOrders = () => {
    let all = [];
    if (activeTab === 'all' || activeTab === 'auto') {
      all = [...all, ...autoOrders];
    }
    if (activeTab === 'all' || activeTab === 'mortgage') {
      all = [...all, ...mortgageOrders];
    }
    
    if (statusFilter !== 'all') {
      all = all.filter(o => o.status === statusFilter);
    }
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      all = all.filter(o => 
        (o.order_id || '').toLowerCase().includes(term) ||
        (o.borrower_name || '').toLowerCase().includes(term) ||
        (o.vehicle_description || '').toLowerCase().includes(term) ||
        (o.property_address || '').toLowerCase().includes(term)
      );
    }
    
    return all.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  };

  const getStatusBadge = (status) => {
    const styles = {
      'pending': 'bg-yellow-500/20 text-yellow-400',
      'pending_consent': 'bg-orange-500/20 text-orange-400',
      'active': 'bg-green-500/20 text-green-400',
      'completed': 'bg-blue-500/20 text-blue-400',
      'cancelled': 'bg-red-500/20 text-red-400'
    };
    return (
      <span className={`px-2 py-1 text-xs rounded ${styles[status] || 'bg-gray-500/20 text-gray-400'}`}>
        {(status || 'unknown').replace(/_/g, ' ').toUpperCase()}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading || user === null) {
    return (
      <div className="min-h-screen bg-[#0a1628] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#b8860b] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading finance orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a1628]" data-testid="admin-finance-orders">
      {/* Header */}
      <header className="border-b border-[#1a2d4a] bg-[#0d1d35]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin-dashboard">
              <Button variant="ghost" size="sm" className="text-[#b8860b] hover:text-white">
                <ArrowLeft className="w-4 h-4 mr-2" />
                ADMIN
              </Button>
            </Link>
            <div className="h-6 w-px bg-[#1a2d4a]" />
            <div>
              <h1 className="text-white text-lg font-light tracking-wide flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#b8860b]" />
                Finance Orders Management
              </h1>
              <p className="text-xs text-gray-500">TIAO & TIMO Finance Agreements</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={fetchAllOrders} data-testid="refresh-orders-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-8" data-testid="order-stats">
          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Total Orders</p>
                  <p className="text-2xl font-bold text-white">{stats.total}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                  <Car className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Auto (TIAO)</p>
                  <p className="text-2xl font-bold text-cyan-400">{stats.auto}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center">
                  <Home className="w-5 h-5 text-indigo-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Mortgage (TIMO)</p>
                  <p className="text-2xl font-bold text-indigo-400">{stats.mortgage}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Pending</p>
                  <p className="text-2xl font-bold text-yellow-400">{stats.pending}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Active</p>
                  <p className="text-2xl font-bold text-green-400">{stats.active}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#b8860b]/20 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-[#b8860b]" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Total Principal</p>
                  <p className="text-lg font-bold text-[#b8860b]">{formatCurrency(stats.total_principal)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="bg-[#0d1d35] border-[#1a2d4a] mb-6">
          <CardContent className="py-4">
            <div className="flex flex-wrap items-center gap-4">
              {/* Type Tabs */}
              <div className="flex gap-2">
                <Button
                  variant={activeTab === 'all' ? 'default' : 'outline'}
                  onClick={() => setActiveTab('all')}
                  className={activeTab === 'all' ? 'bg-[#b8860b]' : 'border-[#1a2d4a] text-gray-400'}
                  size="sm"
                >
                  All ({autoOrders.length + mortgageOrders.length})
                </Button>
                <Button
                  variant={activeTab === 'auto' ? 'default' : 'outline'}
                  onClick={() => setActiveTab('auto')}
                  className={activeTab === 'auto' ? 'bg-cyan-600' : 'border-[#1a2d4a] text-gray-400'}
                  size="sm"
                >
                  <Car className="w-4 h-4 mr-1" />
                  Auto ({autoOrders.length})
                </Button>
                <Button
                  variant={activeTab === 'mortgage' ? 'default' : 'outline'}
                  onClick={() => setActiveTab('mortgage')}
                  className={activeTab === 'mortgage' ? 'bg-indigo-600' : 'border-[#1a2d4a] text-gray-400'}
                  size="sm"
                >
                  <Home className="w-4 h-4 mr-1" />
                  Mortgage ({mortgageOrders.length})
                </Button>
              </div>

              {/* Status Filter */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="bg-[#0a1628] border border-[#1a2d4a] text-white rounded-md px-3 py-2 text-sm"
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="pending_consent">Pending Consent</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
              </select>

              {/* Search */}
              <div className="flex-1 max-w-md">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by order ID, borrower, vehicle, property..."
                    className="pl-10 bg-[#0a1628] border-[#1a2d4a] text-white"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Orders List */}
        <Card className="bg-[#0d1d35] border-[#1a2d4a]">
          <CardHeader>
            <CardTitle className="text-white">Finance Orders</CardTitle>
            <CardDescription>
              {getAllOrders().length} order(s) found
            </CardDescription>
          </CardHeader>
          <CardContent>
            {getAllOrders().length > 0 ? (
              <div className="space-y-4" data-testid="orders-list">
                {getAllOrders().map((order) => (
                  <div 
                    key={order.order_id} 
                    className={`p-4 rounded-lg border ${
                      order.type === 'auto' 
                        ? 'bg-cyan-900/10 border-cyan-700/30' 
                        : 'bg-indigo-900/10 border-indigo-700/30'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          order.type === 'auto' ? 'bg-cyan-500/20' : 'bg-indigo-500/20'
                        }`}>
                          {order.type === 'auto' 
                            ? <Car className="w-5 h-5 text-cyan-400" />
                            : <Home className="w-5 h-5 text-indigo-400" />
                          }
                        </div>
                        <div>
                          <p className="text-white font-medium">
                            {order.type === 'auto' ? 'Auto Financing (TIAO)' : 'Mortgage Financing (TIMO)'}
                          </p>
                          <p className="text-xs text-gray-500 font-mono">{order.order_id}</p>
                        </div>
                      </div>
                      {getStatusBadge(order.status)}
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm mb-3">
                      <div>
                        <p className="text-gray-500 text-xs">Borrower</p>
                        <p className="text-white">{order.borrower_name}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">
                          {order.type === 'auto' ? 'Vehicle' : 'Property'}
                        </p>
                        <p className="text-white text-xs truncate">
                          {order.vehicle_description || order.property_address || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">Principal</p>
                        <p className="text-[#b8860b] font-bold">
                          {formatCurrency(order.principal_amount)}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">Monthly Payment</p>
                        <p className="text-white">{formatCurrency(order.monthly_payment)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">Term</p>
                        <p className="text-white">{order.term_months} months</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4 text-sm mb-3 p-2 bg-[#0a1628]/50 rounded">
                      <div>
                        <p className="text-gray-500 text-xs">APR</p>
                        <p className="text-green-400">{order.indexed_apr}%</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">BETH Collateral</p>
                        <p className="text-[#b8860b]">{parseFloat(order.beth_collateral || 0).toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">Created</p>
                        <p className="text-white">{formatDate(order.created_at)}</p>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 pt-3 border-t border-[#1a2d4a]">
                      <Button size="sm" variant="outline" className="border-[#1a2d4a] text-gray-400">
                        <Eye className="w-4 h-4 mr-1" /> View Details
                      </Button>
                      <Button size="sm" variant="outline" className="border-[#1a2d4a] text-gray-400">
                        <Printer className="w-4 h-4 mr-1" /> Print
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <FileText className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p>No finance orders found</p>
                <p className="text-sm mt-2">
                  {searchTerm || statusFilter !== 'all' 
                    ? 'Try adjusting your filters' 
                    : 'Orders will appear here when users create them'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdminFinanceOrders;
